'use client';

/**
 * Lightweight animated globe — solid disc with rotating orbital + latitude rings.
 * Used for both "historical snapshot loading" and "no snapshot data available" states.
 */
export function SpinningPlanet({ size = 160, label, sublabel }: { size?: number; label?: string; sublabel?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 pointer-events-none select-none">
      <svg width={size} height={size} viewBox="0 0 160 160" style={{ overflow: 'visible' }}>
        <defs>
          <radialGradient id="planet-core" cx="38%" cy="38%" r="75%">
            <stop offset="0%" stopColor="#2a4a7a" stopOpacity="1" />
            <stop offset="55%" stopColor="#0f1d38" stopOpacity="1" />
            <stop offset="100%" stopColor="#040812" stopOpacity="1" />
          </radialGradient>
          <linearGradient id="planet-rim" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
          </linearGradient>
          <radialGradient id="planet-glow" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor="rgba(96,165,250,0)" />
            <stop offset="100%" stopColor="rgba(96,165,250,0.35)" />
          </radialGradient>
          <clipPath id="planet-clip">
            <circle cx="80" cy="80" r="44" />
          </clipPath>
        </defs>

        {/* Outer atmospheric glow */}
        <circle cx="80" cy="80" r="60" fill="url(#planet-glow)" />

        {/* Orbital ring — slowly spins, tilted */}
        <g style={{ transformOrigin: '80px 80px', animation: 'planet-orbit 14s linear infinite' }}>
          <ellipse cx="80" cy="80" rx="70" ry="14" fill="none" stroke="#f59e0b" strokeWidth="1" strokeOpacity="0.55" strokeDasharray="2 4" />
        </g>

        {/* Planet body */}
        <g>
          <circle cx="80" cy="80" r="44" fill="url(#planet-core)" stroke="url(#planet-rim)" strokeWidth="1.2" />

          {/* Rotating latitude / longitude grid, clipped to globe */}
          <g clipPath="url(#planet-clip)"
            style={{ transformOrigin: '80px 80px', animation: 'planet-spin 6s linear infinite' }}>
            {/* Latitude rings (fixed) */}
            <ellipse cx="80" cy="80" rx="44" ry="10" fill="none" stroke="#60a5fa" strokeWidth="0.6" strokeOpacity="0.35" />
            <ellipse cx="80" cy="80" rx="44" ry="22" fill="none" stroke="#60a5fa" strokeWidth="0.6" strokeOpacity="0.25" />
            <ellipse cx="80" cy="80" rx="44" ry="34" fill="none" stroke="#60a5fa" strokeWidth="0.6" strokeOpacity="0.2" />
            {/* Prime meridian + two shifted meridians */}
            <ellipse cx="80" cy="80" rx="6" ry="44" fill="none" stroke="#60a5fa" strokeWidth="0.7" strokeOpacity="0.45" />
            <ellipse cx="80" cy="80" rx="20" ry="44" fill="none" stroke="#60a5fa" strokeWidth="0.6" strokeOpacity="0.3" />
            <ellipse cx="80" cy="80" rx="34" ry="44" fill="none" stroke="#60a5fa" strokeWidth="0.55" strokeOpacity="0.22" />
          </g>

          {/* Subtle highlight crescent */}
          <ellipse cx="68" cy="66" rx="18" ry="10" fill="rgba(255,255,255,0.08)" />
        </g>

        {/* Outer satellite dot riding the orbital ring */}
        <g style={{ transformOrigin: '80px 80px', animation: 'planet-orbit 14s linear infinite' }}>
          <circle cx="150" cy="80" r="1.8" fill="#f59e0b" />
        </g>
      </svg>

      {(label || sublabel) && (
        <div className="text-center">
          {label && <div className="text-[11px] font-mono tracking-wider text-[#cbd5e1]">{label}</div>}
          {sublabel && <div className="text-[9px] font-mono text-[#64748b] mt-1">{sublabel}</div>}
        </div>
      )}

      <style jsx>{`
        @keyframes planet-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes planet-orbit {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
